import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, CheckCircle2, Undo2, ShieldCheck, KeyRound, Trash2 } from "lucide-react";
import { CodeInput, isCodeReady, RecoveryCodesList, MfaSetupConfirm } from "@/components/MfaWidgets";

const btnGhost = "flex-1 border border-black/30 bg-white px-4 py-2 text-sm hover:bg-black hover:text-white";
const btnDark = "flex-1 inline-flex items-center justify-center gap-2 border border-black bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-50";
const btnDanger = "flex-1 inline-flex items-center justify-center gap-2 border-2 border-red-700 bg-red-600 px-4 py-2 text-sm font-black uppercase tracking-widest text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40";

function Modal({ testId, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid={testId} onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto border border-black bg-white p-6" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function StepHead({ step, total, kicker, title }) {
  return (
    <>
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[#FF3B30]">
        <span>{kicker}</span>
        {total ? <span className="font-mono text-zinc-400" data-testid="delete-step-indicator">Step {step} of {total}</span> : null}
      </div>
      <h3 className="mt-1 font-display text-2xl font-black tracking-tighter">{title}</h3>
    </>
  );
}

// Asks for the caller's own authenticator / recovery code; walks them through setup first if needed.
export function MfaCodeStep({ code, setCode }) {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null);
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/mfa/status").then(({ data }) => setEnabled(data.enabled)).catch(() => setEnabled(false));
  }, []);

  async function startSetup() {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start setup"); }
    finally { setBusy(false); }
  }

  if (enabled === null) return <div className="mt-4 text-sm text-zinc-500">Checking authenticator…</div>;
  if (enabled) {
    return (
      <div className="mt-4" data-testid="mfa-code-step">
        {newCodes ? <div className="mb-3"><RecoveryCodesList codes={newCodes} /></div> : null}
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-700"><KeyRound className="h-3.5 w-3.5" /> Authorisation code</label>
        <p className="mt-1 text-xs text-zinc-500">Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p>
        <CodeInput value={code} onChange={setCode} testId="mfa-auth-code-input" />
      </div>
    );
  }
  return (
    <div className="mt-4 border border-[#002FA7]/25 bg-[#002FA7]/[0.04] p-4" data-testid="mfa-setup-required">
      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#002FA7]"><ShieldCheck className="h-4 w-4" /> Authenticator needed</div>
      <p className="mt-1 text-xs text-zinc-700">This action needs a code from an authenticator app (Google Authenticator, Authy, 1Password). Set it up once — it only takes a minute.</p>
      {!setup ? (
        <button onClick={startSetup} disabled={busy} data-testid="mfa-setup-start-btn" className="mt-3 inline-flex items-center gap-2 border border-[#002FA7] bg-[#002FA7] px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-[#002FA7] disabled:opacity-50">
          {busy ? "Preparing…" : "Set up authenticator"}
        </button>
      ) : (
        <MfaSetupConfirm setup={setup} onEnabled={(codes) => { setNewCodes(codes); setEnabled(true); setSetup(null); }} />
      )}
    </div>
  );
}

function BackupStep({ event, choice, setChoice, downloaded, setDownloaded }) {
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const res = await api.get(`/admin/events/${event.id}/export/volunteers`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${event.slug || "event"}-backup-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("Backup downloaded — keep this file safe");
    } catch (e) { toast.error(e.response?.data?.detail || "Backup download failed"); }
    finally { setBusy(false); }
  }
  const opt = (val, title, hint) => (
    <label className="flex cursor-pointer items-start gap-2 border border-black/15 bg-zinc-50 p-3 text-sm" data-testid={`backup-choice-${val}-label`}>
      <input type="radio" name="backup-choice" checked={choice === val} onChange={() => { setChoice(val); setDownloaded(false); }} data-testid={`backup-choice-${val}`} className="mt-0.5" />
      <span><b>{title}</b><div className="mt-0.5 text-[11px] italic text-zinc-500">{hint}</div></span>
    </label>
  );
  return (
    <>
      <p className="mt-3 text-sm text-zinc-700">Would you like to download a final Excel backup of "<b>{event.name}</b>" right now, just in case?</p>
      <div className="mt-4 space-y-2" role="radiogroup">
        {opt("yes", "Yes — download a backup .xlsx first", "Full volunteer roster with every field.")}
        {opt("no", "No — skip the backup", "I already have a copy, or I don't need one.")}
      </div>
      {choice === "yes" && !downloaded ? (
        <button onClick={download} disabled={busy} data-testid="download-backup-btn" className={`${btnDark} mt-3 w-full`}>
          <Download className="h-4 w-4" /> {busy ? "Preparing..." : "Download backup .xlsx"}
        </button>
      ) : null}
      {choice === "yes" && downloaded ? (
        <div className="mt-3 flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800" data-testid="backup-downloaded-note">
          <CheckCircle2 className="h-3.5 w-3.5" /> Backup downloaded.
        </div>
      ) : null}
    </>
  );
}

// Multi-step delete: 1 confirm scope → 2 backup → 3 choose bin / forever (+ authorisation code).
export function DeleteEventDialog({ event, isSuperAdmin, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [purge, setPurge] = useState(true);
  const [backupChoice, setBackupChoice] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [mode, setMode] = useState("bin");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const needsCode = mode === "forever" || (isSuperAdmin && mode === "bin");
  const canArchive = event.status !== "archived";
  const backupReady = backupChoice === "no" || (backupChoice === "yes" && downloaded);

  async function submit() {
    setBusy(true);
    try {
      if (mode === "archive") {
        await api.post(`/admin/events/${event.id}/archive`);
        toast.success(`"${event.name}" archived — nothing was deleted.`);
        onDone({ forever: false, archived: true });
      } else if (mode === "forever") {
        await api.post(`/admin/events/${event.id}/delete-forever`, { mfa_code: code, purge_volunteers: purge });
        toast.success(`"${event.name}" permanently deleted.`);
        onDone({ forever: true });
      } else {
        const { data } = await api.post(`/admin/events/${event.id}/trash`, { mfa_code: code, purge_volunteers: purge });
        onDone({ forever: false, softDeletedId: data.soft_deleted });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail === "MFA_SETUP_REQUIRED" ? "Set up your authenticator first" : (e.response?.data?.detail || "Delete failed"));
    } finally { setBusy(false); }
  }

  return (
    <Modal testId="delete-event-modal" onClose={onClose}>
      {step === 1 ? (
        <>
          <StepHead step={1} total={3} kicker="Confirm delete" title="Are you sure?" />
          <p className="mt-3 text-sm text-zinc-700">You're about to delete "<b>{event.name}</b>"{purge ? " and all the volunteer database information" : ""}.</p>
          <label className="mt-4 flex cursor-pointer items-start gap-2 border border-black/15 bg-zinc-50 p-3 text-xs" data-testid="delete-purge-toggle-label">
            <input type="checkbox" checked={purge} onChange={(e) => setPurge(e.target.checked)} data-testid="delete-purge-toggle" className="mt-0.5" />
            <span>
              <b>Also delete all {event.volunteer_count ?? ""} volunteer records</b>, emails, SMS logs, bounces and link diagnostics tied to this event.
              <div className="mt-1 text-[11px] italic text-zinc-500">Uncheck this to keep volunteer data.</div>
            </span>
          </label>
          <div className="mt-5 flex gap-2">
            <button onClick={onClose} data-testid="delete-cancel-btn" className={btnGhost}>Cancel</button>
            <button onClick={() => setStep(2)} data-testid="delete-continue-btn" className={btnDark}>Continue →</button>
          </div>
        </>
      ) : step === 2 ? (
        <>
          <StepHead step={2} total={3} kicker="Backup" title="Save a backup first?" />
          <BackupStep event={event} choice={backupChoice} setChoice={setBackupChoice} downloaded={downloaded} setDownloaded={setDownloaded} />
          <div className="mt-5 flex gap-2">
            <button onClick={() => setStep(1)} data-testid="delete-back-btn" className={btnGhost}>← Back</button>
            <button onClick={() => setStep(3)} disabled={!backupReady} data-testid="delete-backup-continue-btn" className={btnDark}>
              {backupChoice === null ? "Choose Yes or No" : "Continue →"}
            </button>
          </div>
        </>
      ) : (
        <>
          <StepHead step={3} total={3} kicker="Final step" title="Archive, recycle bin or delete forever?" />
          <div className="mt-4 space-y-2" role="radiogroup">
            {canArchive ? (
              <label className="flex cursor-pointer items-start gap-2 border border-[#002FA7]/25 bg-[#002FA7]/[0.04] p-3 text-sm" data-testid="delete-mode-archive-label">
                <input type="radio" name="delete-mode" checked={mode === "archive"} onChange={() => setMode("archive")} data-testid="delete-mode-archive" className="mt-0.5" />
                <span><b className="text-[#002FA7]">Archive instead</b><div className="mt-0.5 text-[11px] italic text-zinc-500">Nothing is deleted. The event is hidden from the public and kept with all its volunteers; restore it any time.</div></span>
              </label>
            ) : null}
            <label className="flex cursor-pointer items-start gap-2 border border-black/15 bg-zinc-50 p-3 text-sm" data-testid="delete-mode-bin-label">
              <input type="radio" name="delete-mode" checked={mode === "bin"} onChange={() => setMode("bin")} data-testid="delete-mode-bin" className="mt-0.5" />
              <span><b>Move to recycle bin</b><div className="mt-0.5 text-[11px] italic text-zinc-500">Restorable for 24 hours, then wiped automatically.</div></span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 border border-red-200 bg-red-50/50 p-3 text-sm" data-testid="delete-mode-forever-label">
              <input type="radio" name="delete-mode" checked={mode === "forever"} onChange={() => setMode("forever")} data-testid="delete-mode-forever" className="mt-0.5" />
              <span><b className="text-red-700">Delete forever now</b><div className="mt-0.5 text-[11px] italic text-zinc-500">No recycle bin, no undo. Needs your authorisation code.</div></span>
            </label>
          </div>
          {needsCode ? <MfaCodeStep code={code} setCode={setCode} /> : null}
          <div className="mt-5 flex gap-2">
            <button onClick={() => setStep(2)} data-testid="delete-back-btn" className={btnGhost}>← Back</button>
            <button onClick={submit} disabled={busy || (needsCode && !isCodeReady(code))} data-testid="delete-final-btn" className={mode === "archive" ? btnDark : btnDanger}>
              {mode === "archive" ? null : <Trash2 className="h-4 w-4" />} {busy ? "Working…" : mode === "forever" ? "Delete forever" : mode === "archive" ? "Archive event" : "Move to recycle bin"}
            </button>
          </div>
          {mode === "bin" ? (
            <div className="mt-3 flex items-start gap-2 border border-[#002FA7]/20 bg-[#002FA7]/[0.04] px-3 py-2 text-[11px] text-[#002FA7]">
              <Undo2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Restore it any time in the next 24 hours from "Recently deleted" on the events page.</span>
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}

// Purge from the recycle bin: confirm + authorisation code.
export function PurgeEventDialog({ event, onClose, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  async function purge() {
    setBusy(true);
    try {
      await api.post(`/admin/events/${event.id}/purge`, { mfa_code: code });
      toast.success(`"${event.name}" permanently deleted.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail === "MFA_SETUP_REQUIRED" ? "Set up your authenticator first" : (e.response?.data?.detail || "Purge failed"));
    } finally { setBusy(false); }
  }
  return (
    <Modal testId="purge-now-modal" onClose={onClose}>
      <StepHead kicker="Skip recovery window" title="Purge now, no undo?" />
      <p className="mt-3 text-sm text-zinc-700">This will permanently delete "<b>{event.name}</b>" right now. This action <b>cannot be undone</b>.</p>
      <MfaCodeStep code={code} setCode={setCode} />
      <div className="mt-5 flex gap-2">
        <button onClick={onClose} data-testid="purge-cancel-btn" className={btnGhost}>Cancel</button>
        <button onClick={purge} disabled={busy || !isCodeReady(code)} data-testid="purge-now-confirm-btn" className={btnDanger}>
          {busy ? "Purging…" : "Purge forever"}
        </button>
      </div>
    </Modal>
  );
}
